import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { TrendingUp, Info, Menu, X, Sparkles } from 'lucide-react';

export const tabs = [
  { id: 'dashboard', label: 'Dashboard', icon: TrendingUp, enabled: true, path: '/dashboard' },
  { id: 'pricing', label: 'Pricing', icon: Sparkles, enabled: true, path: '/pricing' },
  { id: 'about', label: 'About', icon: Info, enabled: true, path: '/about' },
];

interface NavigationProps {
  headerRef?: React.RefObject<HTMLElement | null>;
  onMenuClose?: () => void;
  mobileActions?: React.ReactNode; // Действия для мобильного меню (Log In, Log Out и т.д.)
}

export interface NavigationRef {
  closeMenu: () => void;
}

/**
 * Навигационное меню - независимый компонент, который живет своей жизнью.
 * Управляет десктопным и мобильным меню, активными табами, состоянием открытия/закрытия.
 */
const Navigation = forwardRef<NavigationRef, NavigationProps>(({ headerRef, onMenuClose, mobileActions }, ref) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuToggleRef = useRef<HTMLButtonElement>(null);

  const isActiveTab = (path: string) => {
    return location.pathname === path;
  };

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
    onMenuClose?.();
  }, [onMenuClose]);

  useImperativeHandle(ref, () => ({ closeMenu: closeMobileMenu }), [closeMobileMenu]);

  // Закрытие при смене роута
  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname, closeMobileMenu]);

  // Закрытие по Escape
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMobileMenu();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [closeMobileMenu]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Позиционирование мобильного меню относительно хедера
  useEffect(() => {
    const updateMenuPosition = () => {
      if (headerRef?.current && menuRef.current) {
        const headerHeight = headerRef.current.offsetHeight;
        menuRef.current.style.top = `${headerHeight}px`;
        menuRef.current.style.maxHeight = `calc(100dvh - ${headerHeight}px)`;
        menuRef.current.style.width = '100vw';
        menuRef.current.style.maxWidth = '100vw';
        menuRef.current.style.minWidth = '100vw';
        menuRef.current.style.left = '0';
        menuRef.current.style.right = '0';
        menuRef.current.style.margin = '0';
        menuRef.current.style.overflowX = 'hidden';
      }
    };

    // Удаляем inline-style margin-left с кнопки hamburger
    const removeMarginLeft = () => {
      if (menuToggleRef.current) {
        const element = menuToggleRef.current;
        if (element.style.marginLeft || element.style.marginLeft !== '0px') {
          element.style.marginLeft = '0px';
        }
        if (element.style.marginLeft !== '0px') {
          element.style.removeProperty('margin-left');
        }
      }
    };

    removeMarginLeft();
    setTimeout(() => {
      updateMenuPosition();
    }, 0);

    window.addEventListener('resize', updateMenuPosition);
    if (mobileMenuOpen) {
      updateMenuPosition();
    }

    return () => {
      window.removeEventListener('resize', updateMenuPosition);
    };
  }, [mobileMenuOpen, headerRef]);

  // Рендер ссылки навигации
  const renderNavLink = (tab: typeof tabs[0], isMobile: boolean = false) => {
    const Icon = tab.icon;
    const isActive = isActiveTab(tab.path);
    
    if (!tab.enabled) {
      return (
        <span
          key={tab.id}
          className={`nav-link nav-link-disabled ${isMobile ? 'nav-link-mobile' : 'nav-link-desktop'}`}
        >
          <Icon className={`nav-link-icon ${isMobile ? 'nav-link-icon-mobile' : 'nav-link-icon-desktop'}`} />
          <span>{tab.label}</span>
          <span className="nav-link-coming-soon">(Coming Soon)</span>
        </span>
      );
    }

    return (
      <Link
        key={tab.id}
        to={tab.path}
        onClick={closeMobileMenu}
        className={`nav-link ${isMobile ? 'nav-link-mobile' : 'nav-link-desktop'} ${isActive ? 'nav-link-active' : ''}`}
      >
        <Icon className={`nav-link-icon ${isMobile ? 'nav-link-icon-mobile' : 'nav-link-icon-desktop'}`} />
        <span>{tab.label}</span>
      </Link>
    );
  };

  const mobileMenuContent = (
    <div 
      ref={menuRef}
      className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}
    >
      <nav className="mobile-nav">
        {tabs.map(tab => renderNavLink(tab, true))}
      </nav>
      {mobileActions && (
        <div className="mobile-actions">
          {mobileActions}
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Desktop Navigation */}
      <nav className="nav nav-desktop">
        {tabs.map(tab => renderNavLink(tab, false))}
      </nav>

      {/* Mobile Menu Toggle Button */}
      <button
        ref={menuToggleRef}
        className="mobile-menu-toggle"
        onClick={() => {
          toggleMobileMenu();
          setTimeout(() => {
            if (headerRef?.current && menuRef.current) {
              const headerHeight = headerRef.current.offsetHeight;
              menuRef.current.style.top = `${headerHeight}px`;
              menuRef.current.style.maxHeight = `calc(100dvh - ${headerHeight}px)`;
              menuRef.current.style.width = '100vw';
              menuRef.current.style.maxWidth = '100vw';
              menuRef.current.style.minWidth = '100vw';
              menuRef.current.style.left = '0';
              menuRef.current.style.right = '0';
              menuRef.current.style.margin = '0';
              menuRef.current.style.paddingTop = '0';
              menuRef.current.style.overflowX = 'hidden';
            }
          }, 10);
        }}
        aria-label="Toggle menu"
      >
        {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Mobile Menu — портал в body: backdrop + меню */}
      {mobileMenuOpen && createPortal(
        <>
          <div
            role="button"
            tabIndex={0}
            aria-label="Close menu"
            onClick={closeMobileMenu}
            onKeyDown={(e) => e.key === 'Enter' && closeMobileMenu()}
            className="mobile-menu-backdrop fixed inset-0 z-[9999]"
          />
          {mobileMenuContent}
        </>,
        document.body
      )}
    </>
  );
});

Navigation.displayName = 'Navigation';
export default Navigation;
